import React, { useState, useEffect } from 'react';
import { EmbedSettings, EmbedPanelProps } from './types';
import {
  PanelContainer,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelContent,
  Section,
  SectionTitle,
  SettingRow,
  SettingLabel,
  Input,
  Select,
  Toggle,
  CodeContainer,
  CodeBlock,
  CopyButton,
  PreviewContainer,
  PreviewFrame,
  Alert,
  Button,
  TabContainer,
  Tab
} from './styled';

const EmbedPanel: React.FC<EmbedPanelProps> = ({
  mapId,
  isPublic,
  shareToken,
  onClose,
  onMakePublic,
  onGenerateShareToken
}) => {
  const [activeTab, setActiveTab] = useState<'settings' | 'preview'>('settings');
  const [copySuccess, setCopySuccess] = useState(false);
  const [settings, setSettings] = useState<EmbedSettings>({
    width: '100%',
    height: '600px',
    showControls: true,
    showBranding: true,
    useShareToken: false,
    allowFullscreen: true,
    border: true,
    theme: 'light'
  });

  // Get the embed URL
  const getEmbedUrl = () => {
    const baseUrl = process.env.REACT_APP_EMBED_URL || 'http://localhost:5173';
    const params = new URLSearchParams();

    if (settings.useShareToken && shareToken) {
      params.append('token', shareToken);
    } else if (isPublic) {
      params.append('mapId', mapId);
    }

    if (!settings.showControls) params.append('hideControls', 'true');
    if (!settings.showBranding) params.append('hideBranding', 'true');

    const queryString = params.toString();
    return queryString ? `${baseUrl}/?${queryString}` : baseUrl;
  };

  // Generate embed code
  const generateEmbedCode = () => {
    const url = getEmbedUrl();
    const borderStyle = settings.border ? 'border: 1px solid #ddd; border-radius: 8px;' : '';
    const fullscreenAttr = settings.allowFullscreen ? 'allowfullscreen' : '';

    return `<iframe
  src="${url}"
  width="${settings.width}"
  height="${settings.height}"
  frameborder="0"
  style="${borderStyle}"
  ${fullscreenAttr}>
</iframe>`;
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle setting changes
  const updateSetting = <K extends keyof EmbedSettings>(
    key: K,
    value: EmbedSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const canEmbed = isPublic || shareToken;

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>Embed Map</PanelTitle>
        <CloseButton onClick={onClose}>×</CloseButton>
      </PanelHeader>

      <PanelContent>
        {!canEmbed && (
          <Alert type="warning">
            <strong>Map is Private</strong><br />
            Your map needs to be public or have a share token to be embedded.
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
              <Button variant="primary" onClick={onMakePublic}>
                Make Public
              </Button>
              <Button variant="secondary" onClick={onGenerateShareToken}>
                Generate Share Token
              </Button>
            </div>
          </Alert>
        )}

        {canEmbed && (
          <>
            <TabContainer>
              <Tab
                active={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
              >
                Settings
              </Tab>
              <Tab
                active={activeTab === 'preview'}
                onClick={() => setActiveTab('preview')}
              >
                Preview
              </Tab>
            </TabContainer>

            {activeTab === 'settings' ? (
              <>
                <Section>
                  <SectionTitle>Dimensions</SectionTitle>
                  <SettingRow>
                    <SettingLabel>Width:</SettingLabel>
                    <Input
                      type="text"
                      value={settings.width}
                      onChange={(e) => updateSetting('width', e.target.value)}
                      placeholder="100% or 800px"
                    />
                  </SettingRow>
                  <SettingRow>
                    <SettingLabel>Height:</SettingLabel>
                    <Input
                      type="text"
                      value={settings.height}
                      onChange={(e) => updateSetting('height', e.target.value)}
                      placeholder="600px"
                    />
                  </SettingRow>
                </Section>

                <Section>
                  <SectionTitle>Display Options</SectionTitle>
                  <SettingRow>
                    <SettingLabel>Show Controls:</SettingLabel>
                    <Toggle
                      active={settings.showControls}
                      onClick={() => updateSetting('showControls', !settings.showControls)}
                    />
                  </SettingRow>
                  <SettingRow>
                    <SettingLabel>Show Branding:</SettingLabel>
                    <Toggle
                      active={settings.showBranding}
                      onClick={() => updateSetting('showBranding', !settings.showBranding)}
                    />
                  </SettingRow>
                  <SettingRow>
                    <SettingLabel>Show Border:</SettingLabel>
                    <Toggle
                      active={settings.border}
                      onClick={() => updateSetting('border', !settings.border)}
                    />
                  </SettingRow>
                  <SettingRow>
                    <SettingLabel>Allow Fullscreen:</SettingLabel>
                    <Toggle
                      active={settings.allowFullscreen}
                      onClick={() => updateSetting('allowFullscreen', !settings.allowFullscreen)}
                    />
                  </SettingRow>
                </Section>

                <Section>
                  <SectionTitle>Access Method</SectionTitle>
                  {shareToken && (
                    <SettingRow>
                      <SettingLabel>Use Share Token:</SettingLabel>
                      <Toggle
                        active={settings.useShareToken}
                        onClick={() => updateSetting('useShareToken', !settings.useShareToken)}
                      />
                    </SettingRow>
                  )}
                  {settings.useShareToken && shareToken && (
                    <Alert type="info">
                      Using share token for private access. The map will be accessible even if it's not public.
                    </Alert>
                  )}
                  {!settings.useShareToken && isPublic && (
                    <Alert type="success">
                      Using public map ID. Anyone can view this map.
                    </Alert>
                  )}
                </Section>

                <Section>
                  <SectionTitle>Embed Code</SectionTitle>
                  <CodeContainer>
                    <CopyButton onClick={copyToClipboard}>
                      {copySuccess ? '✓ Copied!' : 'Copy Code'}
                    </CopyButton>
                    <CodeBlock>{generateEmbedCode()}</CodeBlock>
                  </CodeContainer>
                </Section>

                <Section>
                  <SectionTitle>Direct Link</SectionTitle>
                  <CodeContainer>
                    <CodeBlock>{getEmbedUrl()}</CodeBlock>
                  </CodeContainer>
                </Section>
              </>
            ) : (
              <Section>
                <SectionTitle>Live Preview</SectionTitle>
                <PreviewContainer>
                  <PreviewFrame
                    src={getEmbedUrl()}
                    title="Map Preview"
                    allowFullScreen={settings.allowFullscreen}
                  />
                </PreviewContainer>
                <Alert type="info" style={{ marginTop: '10px' }}>
                  This is how your map will appear when embedded. You can interact with it just like your visitors will.
                </Alert>
              </Section>
            )}
          </>
        )}
      </PanelContent>
    </PanelContainer>
  );
};

export default EmbedPanel;